import { JSX, MouseEventHandler } from 'react';
import { twMerge } from 'tailwind-merge';

import { getAccessibleFocusStyles } from '@videoblocks/design-system';
import { Pause, Play } from '@videoblocks/react-icons';

export interface PlayButtonProps {
    previewUrl: string;
    isPlaying: boolean;
    handlePlayPause: MouseEventHandler<HTMLButtonElement>;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
}

export const PlayButton = ({
    previewUrl,
    isPlaying,
    handlePlayPause,
    size = 'md',
    className = '',
    ...rest
}: PlayButtonProps): JSX.Element => {
    const isSmall = size === 'sm';
    const isMedium = size === 'md';
    const isLarge = size === 'lg';

    const iconClassName = twMerge(isSmall && 'w-4 h-4', isMedium && 'w-5 h-5', isLarge && 'w-6 h-6', 'fill-current');

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if ((event.key === 'Enter' || event.key === ' ') && !event.currentTarget.disabled) {
            event.preventDefault();
            handlePlayPause(event as unknown as React.MouseEvent<HTMLButtonElement>);
        }
    };

    return (
        <button
            className={twMerge(
                'flex items-center justify-center text-black transition-all duration-200 transform hover:scale-105 hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed rounded-full bg-yellow-500',
                getAccessibleFocusStyles(),
                isSmall && 'w-8 h-8',
                isMedium && 'w-10 h-10',
                isLarge && 'w-13 h-13',
                className,
            )}
            onClick={handlePlayPause}
            onKeyDown={handleKeyDown}
            data-testid="play-pause-button"
            disabled={!previewUrl}
            title={!previewUrl ? 'No preview available' : isPlaying ? 'Pause preview' : 'Play preview'}
            {...rest}
        >
            {isPlaying ? <Pause className={iconClassName} /> : <Play className={twMerge(iconClassName)} />}
        </button>
    );
};
